import type { Permission } from 'paintscout'
import { getHighestRole } from '../getHighestRole'
import { getUserRoleFlags } from '../getUserRoleFlags'
import { PAINTER_EMPLOYEE_PERMISSIONS } from './painterAccounts'

export type UserSeatType = 'core' | 'team' | 'painter'
export type PainterRole = 'employee' | 'subcontractor'

export const USER_SEAT_LABELS: Record<UserSeatType, string> = {
  core: 'Sales',
  team: 'Team',
  painter: 'Painter'
}

export const PAINTER_ROLE_LABELS: Record<PainterRole, string> = {
  employee: 'Employee',
  subcontractor: 'Subcontractor'
}

export const USER_SEAT_COLOR_TOKENS: Record<UserSeatType, { dark: string; background: string }> = {
  core: { dark: 'brand.Blue.Blue55', background: 'secondary.260.C95' },
  team: { dark: 'brand.Neutral.P50', background: 'brand.Neutral.P94' },
  painter: { dark: 'secondary.200.C70', background: 'secondary.200.C95' }
}

export function getSeatTypeFromRoles(roles: string[] = []): UserSeatType | undefined {
  const highestRole = getHighestRole(roles)
  if (highestRole === 'core' || highestRole === 'admin' || highestRole === 'sales') return 'core'
  if (highestRole === 'team' || highestRole === 'viewer') return 'team'
  if (highestRole === 'painter') return 'painter'
  return undefined
}

export const NEW_STYLE_ROLES: readonly string[] = ['core', 'team', 'painter']

export function getEffectiveSeatType(args: { seatType?: string; roles?: string[] }): UserSeatType | undefined {
  const roles = args.roles ?? []
  // For new-style roles (core/team/painter), role is the source of truth
  if (roles.some((r) => NEW_STYLE_ROLES.includes(r))) {
    return getSeatTypeFromRoles(roles)
  }
  // For legacy users, prefer explicit seatType if set
  if (args.seatType === 'core' || args.seatType === 'team' || args.seatType === 'painter') {
    return args.seatType
  }
  // Fall back to detecting from legacy roles (e.g. viewer → team, sales → core)
  return getSeatTypeFromRoles(roles)
}

export function getDefaultSeatPermissions(seatType?: UserSeatType, painterRole?: PainterRole): Permission[] {
  if (seatType === 'painter' && painterRole === 'employee') {
    return [...PAINTER_EMPLOYEE_PERMISSIONS]
  }
  if (seatType === 'painter') return []
  return []
}

export function getDefaultRolesForSeatType(seatType: UserSeatType, currentRoles: string[] = []): string[] {
  if (seatType === 'core') return currentRoles.includes('admin') ? ['admin'] : ['sales']
  if (seatType === 'team') return ['viewer']
  if (seatType === 'painter') return ['painter']
  return ['viewer']
}

export function canTransferAccountOwnershipTo(args: { seatType?: string; roles?: string[] }): boolean {
  return getEffectiveSeatType(args) === 'core'
}

/**
 * Roles that can be assigned as the estimator (owner) of a quote/invoice. These are the
 * Sales-seat roles: the new-style `core` plus the legacy `admin` / `sales` (see
 * `getSeatTypeFromRoles`). Team and Painter seats cannot own a document.
 */
export const ESTIMATOR_ROLES = ['admin', 'sales', 'core']

/**
 * Whether a user can be assigned as the estimator of a quote/invoice.
 *
 * A direct role match comes first so a user carrying an extra higher role (e.g.
 * `['admin', 'superadmin']`, where `getHighestRole` reports `superadmin`) still counts. Otherwise
 * the effective seat type decides, which covers legacy users whose seat lives on
 * `app_metadata.seatType` rather than in their roles.
 *
 * Callers filter deactivated users themselves — this only answers the seat question.
 */
export function canBeEstimator(
  user?: {
    app_metadata?: { roles?: string[] | null; seatType?: string | null } | null
  } | null
): boolean {
  const roles = user?.app_metadata?.roles ?? []
  if (roles.some((role) => ESTIMATOR_ROLES.includes(role))) return true
  return getEffectiveSeatType({ seatType: user?.app_metadata?.seatType ?? undefined, roles }) === 'core'
}

/**
 * Roles that can be assigned a quote or invoice from "Share with Staff".
 *
 * The office seats belong here: the new-style `core` / `team`, their legacy equivalents
 * `admin` / `sales` / `viewer` (see `getSeatTypeFromRoles`), and `crew`, which predates seat
 * types and maps to no seat — `devops/cli/tasks/migrate-roles-old-to-new.ts` skips any company
 * that still has one.
 *
 * The new-style `painter` role is deliberately absent (ENG-1995). A painter reaches a job
 * through the painter view and production events, not through "Share with Staff", so listing
 * painters here only made the dialog longer. Legacy painters carry `crew`, not `painter`, and
 * stay listed — they were listed before the seat roles existed.
 *
 * The cross-company roles (`superadmin`, `support`, `corporate`, `paintscout_sales`) are
 * deliberately absent. `searchUsers` already drops `superadmin` rows server-side.
 */
export const STAFF_ROLES: readonly string[] = ['core', 'team', 'admin', 'sales', 'viewer', 'crew']

/**
 * Whether a user can be assigned a document from "Share with Staff".
 *
 * This answers the seat question only. Callers filter out the current user, the document owner,
 * and deactivated users themselves.
 *
 * Note this does **not** exclude users who already hold `read:all-documents`. Assignment grants
 * more than read access: `assignedUserNotify` sends them a `quote-assigned` notification, which
 * a `team` seat can never receive otherwise, because `TEAM_BASE_PERMISSIONS` gives every team
 * user `read:all-documents` (#23972).
 *
 * It does exclude `painter` seats — see `STAFF_ROLES` (ENG-1995). The check is role-based
 * because the `searchUsers` payload that feeds the dialog carries `app_metadata.roles` but not
 * `app_metadata.seatType` (`packages/@paintscout/api/src/auth.graphql`).
 *
 * The `painter` check runs first, so a user who carries `painter` alongside a legacy role during
 * a partial migration is still excluded. This follows `getUserRoleFlags`, where a new-style role
 * suppresses the legacy flags (`isCrew`, `isViewer`, ...) carried beside it. Routing through
 * `getUserRoleFlags` keeps the painter role token defined in one place.
 */
export function canBeAssignedStaff(
  user?: {
    app_metadata?: { roles?: string[] | null } | null
  } | null
): boolean {
  const roles = user?.app_metadata?.roles ?? []
  if (getUserRoleFlags(roles).isPainter) return false
  return roles.some((role) => STAFF_ROLES.includes(role))
}

/**
 * Roles listed in User Management (`TeamNext`).
 *
 * The legacy seat roles `admin` / `sales` / `viewer` are listed beside the new-style ones because
 * about a thousand users still hold them (ENG-2916): leaving them out hid a company's partner from
 * its owner. `getSeatTypeFromRoles` maps each to a seat, and editing one through `UserDialogNext`
 * moves it to the new-style role (see `getLegacyRoleMigrationPermissions` in
 * `react-quote`'s `UserDialog/legacyRoleMigration.ts`).
 *
 * `crew` stays out: it maps to no seat, and nobody may grant it (ENG-2167).
 */
export const USER_MANAGEMENT_ROLES: readonly string[] = [
  'corporate',
  'core',
  'team',
  'painter',
  'admin',
  'sales',
  'viewer'
]
